import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { fetchFaqs } from '../services/faqService';
import '../styles/faqs.css';

const normalize = (value) =>
  (value || '').toString().trim().toLowerCase();

const parseCategoriesOrder = (categories) => {
  if (!categories) return [];

  return categories
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const safeText = (...candidates) =>
  candidates.find((value) => typeof value === 'string' && value.trim().length) || '';

const stripHtmlToText = (html) => {
  if (!html) return '';

  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
};

const extractFaqItemsFromApi = (apiGroups) => {
  if (!Array.isArray(apiGroups)) return [];

  const seenQuestions = new Set();
  const items = [];

  apiGroups.forEach((group) => {
    const content = Array.isArray(group.content) ? group.content : [];

    content.forEach((item) => {
      const question = safeText(item.question, item.Question);
      const answer = safeText(item.answer, item.Answer);

      if (!question || !answer) return;

      const key = normalize(question);
      if (seenQuestions.has(key)) return;

      seenQuestions.add(key);
      items.push({ question, answer });
    });
  });

  return items;
};

const extractFaqItemsFromGroups = (groups) => {
  if (!Array.isArray(groups)) return [];

  const seenQuestions = new Set();
  const items = [];

  groups.forEach((group) => {
    (group.items || []).forEach((item) => {
      const question = item.question?.trim();
      const answer = item.answer?.trim();

      if (!question || !answer) return;

      const key = normalize(question);
      if (seenQuestions.has(key)) return;

      seenQuestions.add(key);
      items.push({ question, answer });
    });
  });

  return items;
};

const buildFaqPageSchema = (items) => {
  const mainEntity = items
    .map(({ question, answer }) => {
      const answerText = stripHtmlToText(answer);
      if (!question || !answerText) return null;

      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText
        }
      };
    })
    .filter(Boolean);

  if (!mainEntity.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity
  };
};

const FaqJsonLd = ({ schema, scriptId }) => {
  useLayoutEffect(() => {
    if (!scriptId) return undefined;

    const removeScript = () => {
      document.getElementById(scriptId)?.remove();
    };

    if (!schema) {
      removeScript();
      return undefined;
    }

    let script = document.getElementById(scriptId);

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(schema);

    return removeScript;
  }, [schema, scriptId]);

  return null;
};

const buildGroupsFromApi = (apiGroups, categoriesOrder) => {
  if (!Array.isArray(apiGroups)) return [];

  // Build a lookup from normalised category name -> group built from API
  const groupsByKey = new Map();

  apiGroups.forEach((group, groupIndex) => {
    const label = safeText(group.categoryName, 'General');
    const key = normalize(label) || `group-${groupIndex}`;
    const items = Array.isArray(group.content) ? group.content : [];

    // Defensive: drop repeated questions within a group in case the API
    // ever returns the same question twice in one category. Different
    // questions sharing the same answer text are intentionally kept.
    const seenQuestions = new Set();

    groupsByKey.set(key, {
      id: `cat-${key}`,
      label,
      items: items
        .filter((item) => {
          const questionKey = normalize(safeText(item.question));
          if (!questionKey || seenQuestions.has(questionKey)) return false;
          seenQuestions.add(questionKey);
          return true;
        })
        .map((item, index) => ({
          id: `faq-${groupIndex}-${index}`,
          question: safeText(item.question),
          answer: safeText(item.answer),
          // Keep original category label for ALL tab badges, etc.
          categoryLabel: safeText(item.category, label)
        }))
    });
  });

  // If categories are configured, always create one tab per configured category,
  // in that exact order, even when there is no API data for it.
  if (categoriesOrder.length) {
    return categoriesOrder.map((rawLabel, idx) => {
      const label = safeText(rawLabel, `Category ${idx + 1}`);
      const key = normalize(label);

      const fromApi = groupsByKey.get(key);
      if (fromApi) {
        return fromApi;
      }

      return {
        id: `cat-${key || idx}`,
        label,
        items: []
      };
    });
  }

  // If no categories configured, just return everything from the API
  return Array.from(groupsByKey.values());
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 1200);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1200);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

const FaqTabs = ({
  name = 'FAQ',
  categories,
  vacabularyName = 'FAQ',
  limit = 5,
  isViewAllPage = false,
  isSearchEnable = false,
  schemaScriptId = 'scl-faqs-jsonld'
}) => {
  const [faqGroups, setFaqGroups] = useState([]);
  const [faqSchemaItems, setFaqSchemaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [activeCategoryId, setActiveCategoryId] = useState(
    null
  );
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const requestPayload = {
          name,
          categories,
          vacabularyName
        };

        const items = await fetchFaqs(requestPayload);
        const order = parseCategoriesOrder(categories);
        const groups = buildGroupsFromApi(items, order);
        const schemaItems = extractFaqItemsFromApi(items);

        if (!cancelled) {
          setFaqGroups(groups);
          setFaqSchemaItems(schemaItems);
          setVisibleCounts({});

          if (!activeCategoryId && groups.length > 0) {
            setActiveCategoryId(groups[0].id);
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError('Failed to load FAQs.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [name, categories, vacabularyName]);

  // Ensure we always have a valid active category and question
  useEffect(() => {
    if (!faqGroups.length) {
      setActiveCategoryId(null);
      setActiveQuestionId(null);
      return;
    }

    let category =
      faqGroups.find((group) => group.id === activeCategoryId) ||
      faqGroups[0];

    if (category.id !== activeCategoryId) {
      setActiveCategoryId(category.id);
    }

    if (!category.items.length) {
      setActiveQuestionId(null);
      return;
    }

    const exists = category.items.some(
      (item) => item.id === activeQuestionId
    );

    if (!exists) {
      setActiveQuestionId(category.items[0].id);
    }
  }, [faqGroups, activeCategoryId, activeQuestionId]);

  const faqPageSchema = useMemo(() => {
    if (loading || error) return null;

    const items = faqSchemaItems.length
      ? faqSchemaItems
      : extractFaqItemsFromGroups(faqGroups);

    return buildFaqPageSchema(items);
  }, [faqSchemaItems, faqGroups, loading, error]);

  const categoriesToShow = useMemo(
    () => faqGroups.map((group) => ({ id: group.id, label: group.label })),
    [faqGroups]
  );

  const activeGroup = faqGroups.find(
    (group) => group.id === activeCategoryId
  );

  const activeFaqs = activeGroup ? activeGroup.items : [];

  const lowerSearch = normalize(searchTerm);

  const filteredFaqs = lowerSearch
    ? activeFaqs.filter((item) => {
        const haystack = [
          item.question,
          item.answer,
          item.categoryLabel
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(lowerSearch);
      })
    : activeFaqs;

  const baseLimit = limit > 0 ? limit : filteredFaqs.length;

  const currentVisibleCount = isViewAllPage
    ? (visibleCounts[activeCategoryId] || baseLimit)
    : baseLimit;

  const canLoadMore =
    isViewAllPage && currentVisibleCount < filteredFaqs.length;

  const handleLoadMore = () => {
    if (!isViewAllPage || !activeCategoryId) return;

    setVisibleCounts((prev) => {
      const current = prev[activeCategoryId] || baseLimit;
      const next = Math.min(current + baseLimit, filteredFaqs.length);
      return {
        ...prev,
        [activeCategoryId]: next
      };
    });
  };

  if (loading) {
    return (
      <section className="scl-faqs">
        <div className='layout-spacing'>
          <p className="scl-faqs__subtitle">
            Loading FAQs, please wait...
          </p>
        </div>        
      </section>
    );
  }

  if (error) {
    return (
      <section className="scl-faqs">
         <div className='layout-spacing'>
            <p className="scl-faqs__subtitle scl-faqs__empty">
              {error}
            </p>
          </div>
      </section>
    );
  }

  return (
    <section className="scl-faqs">
      <FaqJsonLd schema={faqPageSchema} scriptId={schemaScriptId} />
      <div className='layout-spacing'>
        {isSearchEnable && (
          <div className="scl-faqs__search-wrapper">
            <input
              type="text"
              className="scl-faqs__search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Reset visible count for current category when searching
                setVisibleCounts((prev) => ({
                  ...prev,
                  [activeCategoryId]: undefined
                }));
              }}
            />
            <img className ="icon-search-faq" src="/documents/d/global/magnifying-glass" alt ="icon-search" loading='lazy'/>
          </div>
        )}
        <div className="scl-faqs__tab-list" role="tablist">
          {categoriesToShow.map((category) => (
            <button
              key={category.id}
              role="tab"
              type="button"
              className={
                'scl-faqs__tab' +
                (category.id === activeCategoryId
                  ? ' scl-faqs__tab--active'
                  : '')
              }
              onClick={() => setActiveCategoryId(category.id)}
            >
              {category.label}
            </button>
          ))}
        </div>
        <div className="scl-faqs__layout">
          <div className="scl-faqs__layout_top">
            {faqGroups.map((group) => {
              // SEO requirement: all tabs, questions and answers are present
              // in the DOM once at page load. Closed/inactive content is
              // hidden with CSS, not unmounted. Each answer renders exactly
              // once per layout because only ONE layout (mobile accordion OR
              // desktop panel) is mounted, based on viewport width.
              const isActiveCategory = group.id === activeCategoryId;
              const isGroupAllTab =
                normalize(group.label) === 'all';
              const groupFaqs = isActiveCategory ? filteredFaqs : group.items;
              const paginationLimit = isActiveCategory
                ? currentVisibleCount
                : groupFaqs.length;

              return (
                <div
                  key={group.id}
                  className="scl-faqs__category-content"
                  hidden={!isActiveCategory}
                >
                  <div className="scl-faqs__tabs">
                    <div className="scl-faqs__questions">
                      {groupFaqs.map((faq, index) => {
                        const isActiveQuestion =
                          isActiveCategory && faq.id === activeQuestionId;
                        // Items beyond the pagination limit stay in the DOM
                        // (hidden); "Load More" only unhides them.
                        const isPaginatedHidden = index >= paginationLimit;

                        return (
                          <React.Fragment key={faq.id}>
                            <button
                              type="button"
                              className={
                                'scl-faqs__item' +
                                (isActiveQuestion
                                  ? ' scl-faqs__item--active'
                                  : '')
                              }
                              onClick={() => setActiveQuestionId(faq.id)}
                              hidden={isPaginatedHidden}
                            >
                              <div className="scl-faqs__question-row">
                                <div className="scl-faqs__question-text small-body medium neutral1000">
                                  {faq.question}
                                </div>
                                {isGroupAllTab && faq.categoryLabel && (
                                  <span className="scl-faqs__question-tag">
                                    {faq.categoryLabel}
                                  </span>
                                )}
                                <span className="scl-faqs__chevron">
                                  <img
                                    src="/documents/d/global/closedarrow"
                                    alt="dropdownarrows"
                                  />
                                </span>
                              </div>
                            </button>
                            {/* Mobile accordion answer: always in the DOM on
                                mobile; hidden via CSS until opened. */}
                            {isMobile && (
                              <div
                                className={
                                  'scl-faqs__mobile-answer' +
                                  (!isActiveQuestion
                                    ? ' scl-faqs__answer--hidden'
                                    : '')
                                }
                                hidden={isPaginatedHidden}
                              >
                                <div className="divider"></div>
                                <div className="scl-faqs__panel-card">
                                  <div
                                    className="scl-faqs__answer regular-body regular neutral1000"
                                    dangerouslySetInnerHTML={{
                                      __html: faq.answer
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            <div
                              className="divider"
                              hidden={isPaginatedHidden}
                            />
                          </React.Fragment>
                        );
                      })}

                      {isActiveCategory && groupFaqs.length === 0 && (
                        <p className="scl-faqs__empty">
                          No FAQs available for this category yet.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Desktop answer panel: all answers in the DOM; only the
                      active one is visible, the rest are CSS-hidden. */}
                  {!isMobile && (
                    <div className="scl-faqs__panel">
                      <div className="scl-faqs__panel-card">
                        {groupFaqs.length > 0 ? (
                          groupFaqs.map((faq) => {
                            const isActiveQuestion =
                              isActiveCategory &&
                              faq.id === activeQuestionId;

                            return (
                              <div
                                key={faq.id}
                                className={
                                  'scl-faqs__answer regular-body regular neutral1000' +
                                  (!isActiveQuestion
                                    ? ' scl-faqs__answer--hidden'
                                    : '')
                                }
                                dangerouslySetInnerHTML={{
                                  __html: faq.answer
                                }}
                              />
                            );
                          })
                        ) : (
                          <p className="scl-faqs__empty"></p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {canLoadMore && (
              <button
                type="button"
                className="scl-faqs__view-more"
                onClick={handleLoadMore}
              >
                Load More
              </button>
            )}
        </div>
      </div>
    </section>
  );
};

export default FaqTabs;